import { useEffect, useRef } from 'react';

import type { SchemaLoanRequestResolver_Find } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';

import { useSearch } from './contexts/SearchContext';
import { SearchResult } from './SearchResult';
import Spinner from '~/components/icons/Spinner';

export const SearchResults = () => {
    const { t } = useTranslation();
    const { fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, searchFields, searchQuery, searchResult } =
        useSearch();
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;
        const root = sentinel.closest<HTMLElement>('#search-flyout');
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasNextPage) fetchNextPage();
            },
            { root, rootMargin: '0px 0px 100px 0px', threshold: 0 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [fetchNextPage, hasNextPage, searchResult]);

    // no input
    if ((searchQuery === null || searchQuery.trim().length === 0) && Object.keys(searchFields).length === 0) {
        return <div className="text-xs text-grey py-8 px-2 text-center">{t('LEADS.SEARCH.NO_INPUT')}</div>;
    }

    // loading
    if (
        ((searchQuery !== null && searchQuery.trim().length > 0) || Object.keys(searchFields).length > 0) &&
        isFetching &&
        searchResult.length === 0
    ) {
        return (
            <div className="text-xs text-grey py-8 px-2 flex items-center justify-center">
                <Spinner />
            </div>
        );
    }

    // no results
    if (
        ((searchQuery !== null && searchQuery.trim().length > 0) || Object.keys(searchFields).length > 0) &&
        searchResult.length === 0
    ) {
        return (
            <div className="text-xs text-grey py-8 px-2 text-center">
                {t('LEADS.SEARCH.NO_RESULTS', {
                    search:
                        searchQuery ||
                        Object.entries(searchFields)
                            .filter(([_, value]) => value !== null)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(', '),
                })}
            </div>
        );
    }

    // show results
    if (
        ((searchQuery !== null && searchQuery.trim().length > 0) || Object.keys(searchFields).length > 0) &&
        searchResult.length > 0
    ) {
        return (
            <>
                <table className="w-full table-auto">
                    <tbody>
                        {searchResult.map((lead: SchemaLoanRequestResolver_Find[number], index: number) => (
                            <SearchResult
                                key={lead.id}
                                lead={lead as SchemaLoanRequestResolver_Find[number]}
                                brightness={index % 2 === 1 ? 'dark' : 'light'}
                            />
                        ))}
                    </tbody>
                </table>
                {hasNextPage && <div ref={sentinelRef} className="h-4" />}
                {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-2">
                        <Spinner />
                    </div>
                )}
            </>
        );
    }

    return null;
};
